import { useSelector } from 'react-redux';

// Types
import { DisplayDeliveryDetailsProps } from './DisplayDeliveryDetails.types';

// Config
import displayDeliveryDetailsState from './DisplayDeliveryDetails.state';
import { clearabeeConstants, flowerConstants } from '../../constants/deliveryConstants';
import { getDeliveryInfoText } from '../../utils/delivery/getDeliveryDetails';

// Components
import BodyText from '../body-text';
import Container from '../container';
import Heading from '../heading';

// Styles
import styles from './display-delivery-details.scss';

const DisplayDeliveryDetails = ({
  collectionDetailsTitle = '',
  deliveryDetailsTitle = '',
  orderDetails = {},
}: DisplayDeliveryDetailsProps) => {
  const { deliveries, deliveryInfoText, deliveryMethodText, isClickCollectOrder, isMultiBatch } = orderDetails;

  const { isSingleBatchClearabee, isMultiBatchClearabee, isSingleBatchFlowers, isMultiBatchFlowers } =
    useSelector(displayDeliveryDetailsState);

  const isClearabee = isSingleBatchClearabee || isMultiBatchClearabee;
  const clearabeeMethodText = clearabeeConstants.DESCRIPTION;
  const clearabeeInfoText = clearabeeConstants.ADDITIONAL_INFORMATION;

  const isFlowers = isSingleBatchFlowers || isMultiBatchFlowers;
  const flowerMethodText = flowerConstants.DESCRIPTION;
  const flowerInfoText = flowerConstants.ADDITIONAL_INFORMATION[0];

  const renderDeliveryInfoText = (deliveryInfoText: string | Record<string, unknown> | undefined) => {
    const text =
      deliveryInfoText !== null && typeof deliveryInfoText === 'object'
        ? getDeliveryInfoText(deliveryInfoText)
        : deliveryInfoText;

    return (
      <Container maskPercy testId="delivery-details-message">
        {isFlowers && (
          <>
            {isSingleBatchFlowers && flowerInfoText}

            {isMultiBatchFlowers && (
              <>
                <p>{text}</p>
                <p>{flowerInfoText}</p>
              </>
            )}
          </>
        )}

        {isClearabee && (
          <>
            {isSingleBatchClearabee && clearabeeInfoText}

            {isMultiBatchClearabee && (
              <>
                <p>{text}</p>
                <p>{clearabeeInfoText}</p>
              </>
            )}
          </>
        )}

        {!isClearabee && !isFlowers && text}
      </Container>
    );
  };

  const renderDeliveryMethodText = () => {
    if (!isClickCollectOrder) {
      return (
        <BodyText maskPercy noTheme>
          {isSingleBatchFlowers && flowerMethodText}

          {isSingleBatchClearabee && clearabeeMethodText}

          {!isSingleBatchClearabee && !isSingleBatchFlowers && deliveryMethodText}
        </BodyText>
      );
    }

    return renderDeliveryInfoText(deliveryInfoText);
  };

  let chargeText;

  const title = isClickCollectOrder ? collectionDetailsTitle : deliveryDetailsTitle;

  if (!orderDetails.deliveries) {
    return null;
  }

  const charge = deliveries?.[0]?.charge;
  if (!isMultiBatch && !isSingleBatchClearabee && !isSingleBatchFlowers) {
    chargeText = charge;
  }

  return (
    <div className={styles.container} data-testid="delivery-details">
      <Heading label={title} marginBottom="1" tag="h3" type="xs" />

      <dl className={styles.deliveryValues}>
        <dt data-testid="delivery-details-title">{renderDeliveryMethodText()}</dt>
        {!isMultiBatch && charge && (
          <dd data-testid="delivery-details-charge">
            <BodyText aria-hidden="true" maskPercy noTheme tag="span" uppercase>
              {chargeText}
            </BodyText>
            <span className={styles.visuallyHiddenText}>{`Delivery charge ${chargeText}`}</span>
          </dd>
        )}
      </dl>

      {!isClickCollectOrder && renderDeliveryInfoText(deliveryInfoText)}
    </div>
  );
};

export default DisplayDeliveryDetails;
